/** System status → Services: every running service by kind, queues, the database and external services. */
import { Fragment } from 'react';
import { LOG_SERVICE_TARGETS } from '@prism/contracts';
import { bytes, elapsed, relative } from '../../../lib/format';
import { Dot, Usage, type ResourceUse, type StatusDTO } from './kit';
import { TableScroll } from '../../../components/TableScroll';

type Instance = StatusDTO['services'][number];

/** The service's name as the log-level settings call it. */
function serviceLabel(target: string): string {
  return LOG_SERVICE_TARGETS.find((t) => t.target === target)?.label ?? target;
}

/** What each kind of PRISM service does, for people reading the list. */
const SERVICE_KINDS: Array<{ key: string; label: string; help: string; match: (x: Instance) => boolean }> = [
  { key: 'api', label: 'API', help: 'Serves the web app’s requests and the integration API, media streaming, permissions, storage and storage agents; runs the background jobs. Each API process shows its role: requests, background work, both, or the portal’s supplier API', match: (x) => x.service === 'prism-api' },
  { key: 'workflow', label: 'Workflow service', help: 'Runs ingest, export, transfer and automation workflows (on Temporal) and relays events', match: (x) => x.service === 'prism-workflow' },
  { key: 'process', label: 'Media processing workers', help: 'Inspect new media and make proxies, thumbnails, scrub images and waveforms', match: (x) => x.service === 'prism-media-worker' && roles(x).length === 1 && roles(x)[0] === 'process' },
  { key: 'transcode', label: 'Transcoding and rendering workers', help: 'Render sequence exports and workflow transcodes', match: (x) => x.service === 'prism-media-worker' && roles(x).length === 1 && roles(x)[0] === 'transcode' },
  { key: 'transfer', label: 'Transfer workers', help: 'Copy and move files between storage, and wait on external transcoders', match: (x) => x.service === 'prism-media-worker' && roles(x).length === 1 && roles(x)[0] === 'transfer' },
  { key: 'graphics', label: serviceLabel('graphics-renderer'), help: 'Renders HTML5 graphics for previews and exports', match: (x) => x.service === 'prism-graphics-renderer' },
  { key: 'live', label: 'Live recording', help: serviceLabel('media-worker:live') + ': records live sources into assets', match: (x) => x.service === 'prism-media-worker' && roles(x).length === 1 && roles(x)[0] === 'live' },
  { key: 'all', label: 'Media workers (all kinds of work)', help: 'Workers that take processing, transcoding and transfers', match: (x) => x.service === 'prism-media-worker' && roles(x).length !== 1 },
];
const roles = (x: Instance) => ((x.detail.roles as Array<{ role: string }> | undefined) ?? []).map((r) => r.role);

/** An API process's PRISM_API_ROLE (1.8.3), from its heartbeat: what it does. */
const API_ROLES: Record<string, string> = {
  all: 'All (requests and background work)',
  serve: 'Serve (requests)',
  jobs: 'Jobs (background work)',
  portal: 'Portal (supplier API)',
};
function apiRole(x: Instance): string {
  const role = typeof x.detail.role === 'string' ? x.detail.role : '';
  return API_ROLES[role] ?? (role || '—');
}

export function Services({ s }: { s: StatusDTO }) {
  const groups = SERVICE_KINDS.map((k) => ({ ...k, items: s.services.filter(k.match) }));
  const other = s.services.filter((x) => !SERVICE_KINDS.some((k) => k.match(x)));
  if (other.length) groups.push({ key: 'other', label: 'Other services', help: '', match: () => false, items: other });
  const graphics = s.queues.filter((q) => q.taskQueue.startsWith('prism-graphics@'));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {groups.filter((g) => g.items.length).map((g) => (
        <section className="panel-card" key={g.key} data-testid={`service-group-${g.key}`}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <p className="section-label" style={{ margin: 0 }}>{g.label}</p>
            <Dot ok={g.items.every((x) => x.healthy)} warn label={`${g.items.filter((x) => x.healthy).length} of ${g.items.length} running`} />
          </div>
          {g.help && <span className="help">{g.help}</span>}
          <TableScroll>
          <table className="table compact" data-testid="service-instances">
            <thead><tr>{g.key === 'other' && <th>Service</th>}<th>Instance</th>{g.key === 'api' && <th>Role</th>}<th>State</th><th>Up</th><th>Resources</th><th>Version</th><th>Errors 24 h</th></tr></thead>
            <tbody>{g.items.map((x) => (
              <tr key={`${x.service}/${x.instance}`}>
                {g.key === 'other' && <td className="wrap">{LOG_SERVICE_TARGETS.find((t) => t.service === x.service)?.label ?? x.service}</td>}
                <td className="mono faint wrap">{x.instance}</td>
                {g.key === 'api' && <td className="nowrap" data-testid="api-role">{apiRole(x)}</td>}
                <td className="nowrap"><Dot ok={x.healthy} label={x.healthy ? 'Running' : `Silent ${relative(x.lastSeenAt)}`} /></td>
                <td className="muted nowrap">{elapsed(x.startedAt, null)}</td>
                <td className="nowrap"><Usage r={x.detail.resources as ResourceUse | undefined} /></td>
                <td className="muted wrap">{x.version ?? '—'} · {String(x.detail.node ?? '')}</td>
                <td className="mono nowrap" style={{ color: s.errors24h[x.service] ? 'var(--danger)' : undefined }}>{s.errors24h[x.service] ?? 0}</td>
              </tr>
            ))}</tbody>
          </table>
          </TableScroll>
        </section>
      ))}
      {s.services.length === 0 && <p className="muted">No reports yet.</p>}
      <section className="panel-card">
        <p className="section-label">Not reporting here</p>
        <dl className="kv">
          <dt>Web tier</dt><dd>Serves the app and forwards requests to the API (you are using it now)</dd>
          <dt>Graphics renderers</dt><dd>{graphics.length ? graphics.map((q) => <span key={q.taskQueue} style={{ marginRight: 10 }}><Dot ok={q.healthy} label={`${q.taskQueue.split('@')[1]}: ${q.pollers || 'none'} polling`} /></span>) : 'None polling'} <span className="faint">· isolated, seen through their queue</span></dd>
        </dl>
        <span className="help">Only current instances are shown{s.replacedInstances ? ` (${s.replacedInstances} replaced by a newer instance after a restart or rollout are hidden)` : ''}. Services report every 15 seconds. Resources are for the whole container (including FFmpeg) when PRISM runs in containers, otherwise for the service’s own process. Media workers at sites that cannot reach the API appear only under worker queues.</span>
      </section>
      <section className="panel-card">
        <p className="section-label">Worker queues (Temporal)</p>
        <TableScroll>
        <table className="table compact">
          <thead><tr><th>Queue</th><th>Serves</th><th>Workers</th><th>Last poll</th></tr></thead>
          <tbody>{s.queues.map((q) => (
            <tr key={q.taskQueue}>
              <td className="mono wrap">{q.taskQueue}</td><td className="muted wrap">{q.kind}</td>
              <td className="wrap" style={{ minWidth: 200 }}><Dot ok={q.healthy} label={q.pollers ? `${q.pollers}` : 'none'} /> <span className="faint mono" style={{ fontSize: 11 }}>{q.identities.join(', ')}</span></td>
              <td className="muted nowrap">{relative(q.lastPollAt)}</td>
            </tr>
          ))}</tbody>
        </table>
        </TableScroll>
      </section>
      <div className="cards">
        <section className="panel-card">
          <p className="section-label">Database</p>
          <dl className="kv">
            <dt>PostgreSQL</dt><dd>{s.database.version}</dd>
            <dt>Latency</dt><dd>{s.database.latencyMs} ms</dd>
            <dt>Size</dt><dd>{bytes(s.database.sizeBytes)}</dd>
            <dt>Connections</dt><dd>{s.database.connections}</dd>
            <dt>Event relay</dt><dd>{s.outbox.pending} waiting{s.outbox.pending ? ` · oldest ${s.outbox.oldest} s` : ''}</dd>
          </dl>
        </section>
        {s.clamav && (
          <section className="panel-card" data-testid="service-clamav">
            <p className="section-label">Virus scanning (ClamAV)</p>
            <dl className="kv">
              <dt>State</dt><dd><Dot ok={Boolean(s.clamav.reachable)} label={s.clamav.reachable ? 'Answering' : s.clamav.error ?? 'Not answering'} /></dd>
              {s.clamav.latencyMs != null && <><dt>Response</dt><dd>{s.clamav.latencyMs} ms</dd></>}
              {s.clamav.engine && <><dt>Engine</dt><dd>ClamAV {s.clamav.engine}</dd></>}
              {s.clamav.signatures && <><dt>Signatures</dt><dd>{s.clamav.signatures}{s.clamav.signaturesAt ? ` · ${relative(s.clamav.signaturesAt)}` : ''}</dd></>}
            </dl>
            <span className="help">Scans media portal uploads (PRISM_PORTAL_CLAMAV_URL). Checked by the API{s.clamav.checkedAt ? ` ${relative(s.clamav.checkedAt)}` : ''}, at most once a minute.</span>
          </section>
        )}
        {s.workflowService && (
          <section className="panel-card" data-testid="workflow-service">
            <p className="section-label">Workflow service</p>
            <dl className="kv">
              {s.workflowService.cache && <><dt>Workflow cache</dt><dd>{s.workflowService.cache.cached} of {s.workflowService.cache.max} · {s.workflowService.cache.taskConcurrency} tasks at a time</dd></>}
              {s.workflowService.admission && <>
                <dt>Ingests running</dt><dd>{s.workflowService.admission.ingest.running} of at most {s.workflowService.admission.ingest.max}{s.workflowService.admission.ingest.deferred ? ` · ${s.workflowService.admission.ingest.deferred} held back` : ''}</dd>
                <dt>Transfers running</dt><dd>{s.workflowService.admission.transfer.running} of at most {s.workflowService.admission.transfer.max}{s.workflowService.admission.transfer.deferred ? ` · ${s.workflowService.admission.transfer.deferred} held back` : ''}</dd>
              </>}
              <dt>Workflow tasks waiting</dt><dd>{s.workflowService.backlog ?? '—'}{s.workflowService.backlogAgeSeconds ? ` · oldest ${s.workflowService.backlogAgeSeconds} s` : ''}</dd>
            </dl>
          </section>
        )}
        <section className="panel-card">
          <p className="section-label">Storage agents</p>
          {s.agents.length === 0 ? <p className="muted" style={{ margin: 0 }}>No agents.</p> : (
            <dl className="kv">{s.agents.map((a) => (
              <Fragment key={a.name}><dt>{a.name}</dt><dd><Dot ok={a.connected} warn label={a.connected ? `${a.site} · v${a.version ?? '?'}` : `${a.status} · last seen ${relative(a.lastSeenAt)}`} />{a.queuedJobs ? ` · ${a.queuedJobs} queued` : ''}</dd></Fragment>
            ))}</dl>
          )}
        </section>
        <section className="panel-card">
          <p className="section-label">External transcoder</p>
          {!s.transcoder.configured ? <p className="muted" style={{ margin: 0 }}>Not configured.</p> : (
            <dl className="kv"><dt>State</dt><dd><Dot ok={Boolean(s.transcoder.reachable)} label={s.transcoder.reachable ? 'Reachable' : s.transcoder.error ?? 'Unreachable'} /></dd>
              {s.transcoder.info && <><dt>Engine</dt><dd>{s.transcoder.info.engine}{s.transcoder.info.simulated ? ' (simulator)' : ''}</dd></>}</dl>
          )}
        </section>
        <section className="panel-card">
          <p className="section-label">Speech service</p>
          {!s.speech.configured ? <p className="muted" style={{ margin: 0 }}>Not configured (PRISM_SPEECH_URL is not set), so subtitles cannot be generated.</p> : (
            <dl className="kv"><dt>State</dt><dd><Dot ok={Boolean(s.speech.reachable)} label={s.speech.reachable ? 'Reachable' : s.speech.error ?? 'Unreachable'} /></dd>
              <dt>Engine</dt><dd>Whisper (small, multilingual)</dd></dl>
          )}
        </section>
      </div>
    </div>
  );
}
